import { useEffect, useState } from 'react';

/**
 * Hook to load Google Maps script with the new loader (supports importLibrary)
 * Compatible with @vis.gl/react-google-maps APIProvider
 * @param {string} apiKey - Google Maps API key
 * @returns {boolean} Whether the script is loaded
 */
export const useGoogleMaps = (apiKey) => {
    const [isLoaded, setIsLoaded] = useState(false);

    useEffect(() => {
        // Check if already loaded by @vis.gl/react-google-maps or other loaders
        if (window.google?.maps?.places?.AutocompleteSuggestion) {
            setIsLoaded(true);
            return;
        }

        // Check if Google Maps is loaded (by @vis.gl or other) but places not yet available
        if (window.google?.maps) {
            // Wait for places library to be available
            const checkLoaded = setInterval(() => {
                if (window.google?.maps?.places?.AutocompleteSuggestion) {
                    setIsLoaded(true);
                    clearInterval(checkLoaded);
                }
            }, 100);

            // Timeout after 10 seconds
            setTimeout(() => clearInterval(checkLoaded), 10000);

            return () => clearInterval(checkLoaded);
        }

        // Check if script is already being loaded
        const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
        if (existingScript) {
            // Wait for it to load with places support
            const checkLoaded = setInterval(() => {
                if (window.google?.maps?.places?.AutocompleteSuggestion) {
                    setIsLoaded(true);
                    clearInterval(checkLoaded);
                }
            }, 100);

            // Timeout after 10 seconds
            setTimeout(() => clearInterval(checkLoaded), 10000);

            return () => clearInterval(checkLoaded);
        }

        // Only load script if not already present (avoid conflicts with @vis.gl/react-google-maps)
        const script = document.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&loading=async&libraries=places&v=weekly`;
        script.async = true;
        script.defer = true;

        script.onload = () => {
            // Wait for places library to be available
            const checkLoaded = setInterval(() => {
                if (window.google?.maps?.places?.AutocompleteSuggestion) {
                    setIsLoaded(true);
                    clearInterval(checkLoaded);
                }
            }, 100);

            // Timeout after 10 seconds
            setTimeout(() => clearInterval(checkLoaded), 10000);
        };

        script.onerror = () => {
            console.error('Failed to load Google Maps script');
        };

        document.head.appendChild(script);

        return () => {
            // Cleanup is handled by the script itself
        };
    }, [apiKey]);

    return isLoaded;
};
